import React, { useContext, useState } from 'react';
import { pick } from 'lodash';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Button, Tag } from '@onward-delivery/design';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import IntegrationRow from '@/components/CorporateUsers/modals/IntegrationRow';
import IntegrationsModal from '@/components/CorporateUsers/modals/IntegrationsModal';
import { INTEGRATION_TYPE_LABELS } from '@/components/CorporateUsers/modals/constants';
import { CardRow, SystemCard } from './blocks';
import { TMS_TYPES, requiredFieldLabelsOf, tmsMethodOf, tmsStatusOf } from './utils';

const OVERRIDE_IDENTITY_KEYS = [
    'override_id',
    'carrier_id',
    'middle_mile_carrier_id',
    'client_id',
    'organization_id',
    'is_shipper_default',
];

const ConnectedTms = ({ integration, defaultIntegrations }) => {
    const { client: carrier } = useContext(UserContext);
    const [configuring, setConfiguring] = useState(false);

    const typeLabel = INTEGRATION_TYPE_LABELS[integration.type] || integration.type;
    const status = tmsStatusOf(integration.status);
    const statusTag = status ? <Tag variant={status.variant}>{status.label}</Tag> : '--';
    const lastSync = integration.last_success_at
        ? formatDistanceToNow(parseISO(integration.last_success_at), { addSuffix: true })
        : 'Never';

    const configureModal = configuring ? (
        <IntegrationsModal
            open
            onClose={() => setConfiguring(false)}
            defaultIntegrations={defaultIntegrations}
            carrierId={carrier.client_id}
        />
    ) : null;

    return (
        <SystemCard
            badge={typeLabel.slice(0, 2).toUpperCase()}
            badgeClassName="bg-teal-800"
            title={typeLabel}
            subtitle="their TMS — the one system their whole record syncs from"
            actions={
                <Button variant="outline" size="sm" onClick={() => setConfiguring(true)}>
                    Configure
                </Button>
            }
        >
            <CardRow label="Method">{tmsMethodOf(integration)}</CardRow>
            <CardRow label="Status">{statusTag}</CardRow>
            <CardRow label="Last sync">{lastSync}</CardRow>
            {configureModal}
        </SystemCard>
    );
};

// Saving the draft writes the org default; a MANUAL placeholder default is updated in place rather than duplicated.
const TmsPicker = ({ placeholder }) => {
    const { client: carrier } = useContext(UserContext);
    const { state } = useContext(CorpAdminContext);
    const [draft, setDraft] = useState(null);

    const pickType = (type) => {
        const newDefault = {
            carrier_id: carrier.client_id,
            client_id: carrier.client_id,
            organization_id: state.organizationId,
            is_shipper_default: true,
        };
        const placeholderIdentity = placeholder ? pick(placeholder, OVERRIDE_IDENTITY_KEYS) : null;
        const identity = draft || placeholderIdentity || newDefault;

        setDraft({ ...identity, type, config: {} });
    };

    const chips = TMS_TYPES.map((type) => {
        const isPicked = type === draft?.type;

        return (
            <Button
                key={type}
                variant="outline"
                size="sm"
                aria-pressed={isPicked}
                className={isPicked ? 'border-primary text-primary' : ''}
                onClick={() => pickType(type)}
            >
                {INTEGRATION_TYPE_LABELS[type] || type}
            </Button>
        );
    });

    let draftForm = null;
    if (draft) {
        const requiredLabels = requiredFieldLabelsOf(draft.type);
        draftForm = (
            <>
                <IntegrationRow
                    key={draft.type}
                    integration={draft}
                    onSaved={() => setDraft(null)}
                    onDiscard={() => setDraft(null)}
                    onWritten={(overrideId) => setDraft((current) => ({ ...current, override_id: overrideId }))}
                />
                <span className="w-fit rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] text-slate-500">
                    Needs: <span className="font-semibold text-slate-900">{requiredLabels.join(' · ')}</span>
                </span>
            </>
        );
    }

    return (
        <SystemCard
            badge="?"
            badgeClassName="bg-slate-400"
            title="No TMS connected"
            subtitle="one system per carrier — pick theirs and enter what it asks for"
        >
            <div className="flex flex-col gap-3 bg-slate-50 p-4">
                <div className="flex flex-wrap gap-2">{chips}</div>
                {draftForm}
            </div>
        </SystemCard>
    );
};

const TmsCard = () => {
    const { client: carrier } = useContext(UserContext);
    const { state } = useContext(CorpAdminContext);
    const defaultIntegrations = state.defaultIntegrationsByCarrier[carrier.client_id] || [];

    const connected = defaultIntegrations.find((integration) => integration.type !== 'MANUAL');
    if (connected) return <ConnectedTms integration={connected} defaultIntegrations={defaultIntegrations} />;

    const placeholder = defaultIntegrations.find((integration) => integration.type === 'MANUAL');

    return <TmsPicker placeholder={placeholder} />;
};

export default TmsCard;
